import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfig } from '../configContext.js';
import { useDebounce } from '../useDebounce.jsx';

/**
 * Search-and-select for Zimbra accounts, with the selection shown as chips.
 *
 * The search, its debounce and its results live here rather than in each
 * parent: the same field appears in four places (add a calendar, invite
 * attendees, create a group, edit a group's members), and each copy carried
 * its own searchTerm / options / loading trio.
 *
 * Values are `{ name, uuid }`; identity is the uuid.
 */
export default function AccountPicker({
  value,
  onChange,
  placeholder = 'Rechercher un compte',
  disabledNames,
  disabledLabel = 'Déjà ajouté',
  autoFocus,
}) {
  const { apiUrl } = useConfig();
  const [term, setTerm] = useState('');
  // One piece of state for the search, holding the term it answers. "Loading"
  // and "which options to show" are derived from it, so the effect never has
  // to write state synchronously just to reset them.
  const [result, setResult] = useState({ term: '', options: [] });
  const [active, setActive] = useState(0);
  const debounced = useDebounce(term, 500);
  const containerRef = useRef(null);

  const selectedUuids = useMemo(() => new Set(value.map((v) => v.uuid)), [value]);

  useEffect(() => {
    if (!debounced) return undefined;
    let cancelled = false;
    const params = new URLSearchParams({ filter: debounced });
    fetch(`${apiUrl}/accounts/search?${params}`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setResult({
          term: debounced,
          options: (data.accounts || []).map((e) => ({ name: e.name, uuid: e.id })),
        });
        setActive(0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Search error:', err);
        // Recorded as answered, so the field stops saying "searching".
        setResult({ term: debounced, options: [] });
      });
    // A slow answer to an abandoned search must not overwrite a newer one.
    return () => {
      cancelled = true;
    };
  }, [debounced, apiUrl]);

  // Still waiting whenever the answer on hand is not the one being typed.
  const loading = Boolean(debounced) && result.term !== debounced;
  const options = result.term === debounced ? result.options : [];

  // Close the result list when the focus or the click goes elsewhere.
  useEffect(() => {
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setTerm('');
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const visible = options.filter((o) => !selectedUuids.has(o.uuid));

  const add = (option) => {
    if (disabledNames?.has(option.name)) return;
    onChange([...value, option]);
    // Clearing the term hides the list; the results themselves are derived.
    setTerm('');
  };

  const remove = (uuid) => onChange(value.filter((v) => v.uuid !== uuid));

  const onKeyDown = (event) => {
    if (event.key === 'Escape') return setTerm('');
    if (event.key === 'Backspace' && !term && value.length > 0) {
      return onChange(value.slice(0, -1));
    }
    if (visible.length === 0) return undefined;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      return setActive((i) => (i + 1) % visible.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      return setActive((i) => (i - 1 + visible.length) % visible.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      return add(visible[active]);
    }
    return undefined;
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border border-gray-300 px-2 py-1 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
        {value.map((item) => (
          <span
            key={item.uuid}
            className="flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-900"
          >
            {item.name}
            <button
              type="button"
              onClick={() => remove(item.uuid)}
              aria-label={`Retirer ${item.name}`}
              className="text-brand-700 hover:text-brand-900"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          value={term}
          autoFocus={autoFocus}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={placeholder}
          role="combobox"
          aria-expanded={Boolean(term)}
          aria-controls="account-picker-list"
          className="min-w-[8rem] flex-1 border-0 py-1 text-sm outline-none"
        />
      </div>

      {term && (
        <ul
          id="account-picker-list"
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {loading && <li className="px-3 py-2 text-sm text-gray-500">Recherche en cours…</li>}
          {!loading && visible.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">Aucun compte trouvé</li>
          )}
          {!loading &&
            visible.map((option, index) => {
              const isDisabled = disabledNames?.has(option.name);
              return (
                <li key={option.uuid} role="option" aria-selected={index === active}>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => add(option)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm ${
                      isDisabled
                        ? 'cursor-not-allowed text-gray-400'
                        : `text-gray-700 ${index === active ? 'bg-gray-100' : ''}`
                    }`}
                  >
                    <span className="truncate">{option.name}</span>
                    {isDisabled && (
                      <span className="shrink-0 rounded-full border border-gray-300 px-1.5 text-xs text-gray-500">
                        {disabledLabel}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

AccountPicker.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabledNames: PropTypes.instanceOf(Set),
  disabledLabel: PropTypes.string,
  autoFocus: PropTypes.bool,
};
